/**
 * Color picker for family members
 *
 * Shows a tappable color swatch that opens a modal with:
 * - Hue/saturation color panel for custom color selection
 * - Preset swatches from FAMILY_MEMBER_COLORS for quick selection
 * - Done button to confirm
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import ColorPicker, {
  HueSlider,
  Panel1,
  type ColorFormatsObject,
} from 'reanimated-color-picker';
import { Ionicons } from '@expo/vector-icons';

import { FAMILY_MEMBER_COLORS } from '@/lib/family/FamilyMemberColorPalette';
import { Spacing, BorderRadius } from '@/constants/spacing';

type FamilyMemberColorPickerProps = {
  currentColor: string;
  onColorChanged: (color: string) => void;
  accentColor: string;
  textColor: string;
  textMutedColor: string;
  backgroundColor: string;
  cardBackgroundColor: string;
};

export function FamilyMemberColorPicker({
  currentColor,
  onColorChanged,
  accentColor,
  textColor,
  textMutedColor,
  backgroundColor,
  cardBackgroundColor,
}: FamilyMemberColorPickerProps) {
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedColor, setSelectedColor] = useState(currentColor);

  // Sync when prop changes externally
  useEffect(() => {
    setSelectedColor(currentColor);
  }, [currentColor]);

  const handleOpen = useCallback(() => {
    setSelectedColor(currentColor);
    setModalVisible(true);
  }, [currentColor]);

  const handleDone = useCallback(() => {
    onColorChanged(selectedColor);
    setModalVisible(false);
  }, [selectedColor, onColorChanged]);

  const handleDismiss = useCallback(() => {
    setSelectedColor(currentColor);
    setModalVisible(false);
  }, [currentColor]);

  const handleColorChange = useCallback((result: ColorFormatsObject) => {
    setSelectedColor(result.hex);
  }, []);

  const handlePresetSelect = useCallback((hex: string) => {
    setSelectedColor(hex);
  }, []);

  return (
    <>
      <TouchableOpacity
        style={styles.swatchButton}
        onPress={handleOpen}
        activeOpacity={0.7}
      >
        <View style={[styles.swatch, { backgroundColor: currentColor }]} />
        <Text style={[styles.swatchLabel, { color: textMutedColor }]}>
          Change color
        </Text>
      </TouchableOpacity>

      <ColorPickerModal
        visible={modalVisible}
        selectedColor={selectedColor}
        accentColor={accentColor}
        textColor={textColor}
        textMutedColor={textMutedColor}
        backgroundColor={backgroundColor}
        cardBackgroundColor={cardBackgroundColor}
        onColorChange={handleColorChange}
        onPresetSelect={handlePresetSelect}
        onDone={handleDone}
        onDismiss={handleDismiss}
      />
    </>
  );
}

type ColorPickerModalProps = {
  visible: boolean;
  selectedColor: string;
  accentColor: string;
  textColor: string;
  textMutedColor: string;
  backgroundColor: string;
  cardBackgroundColor: string;
  onColorChange: (result: ColorFormatsObject) => void;
  onPresetSelect: (hex: string) => void;
  onDone: () => void;
  onDismiss: () => void;
};

function ColorPickerModal({
  visible,
  selectedColor,
  accentColor,
  textColor,
  textMutedColor,
  backgroundColor,
  cardBackgroundColor,
  onColorChange,
  onPresetSelect,
  onDone,
  onDismiss,
}: ColorPickerModalProps) {
  const onCompleteWorklet = useCallback((result: ColorFormatsObject) => {
    'worklet';
    runOnJS(onColorChange)(result);
  }, [onColorChange]);

  const backdropOpacity = useSharedValue(0);
  const translateY = useSharedValue(500);

  useEffect(() => {
    if (visible) {
      backdropOpacity.value = withTiming(1, { duration: 200 });
      translateY.value = withTiming(0, { duration: 250 });
    } else {
      backdropOpacity.value = withTiming(0, { duration: 200 });
      translateY.value = withTiming(500, { duration: 200 });
    }
  }, [visible, backdropOpacity, translateY]);

  const backdropStyle = useAnimatedStyle(() => ({
    opacity: backdropOpacity.value,
  }));

  const contentStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }));

  return (
    <Modal
      visible={visible}
      animationType="none"
      transparent
      onRequestClose={onDismiss}
      statusBarTranslucent
    >
      <View style={styles.overlay}>
        <Animated.View style={[styles.backdrop, backdropStyle]}>
          <Pressable style={StyleSheet.absoluteFill} onPress={onDismiss} />
        </Animated.View>

        <Animated.View
          style={[styles.content, { backgroundColor }, contentStyle]}
        >
          {/* Header */}
          <View style={[styles.header, { borderBottomColor: cardBackgroundColor }]}>
            <Text style={[styles.headerTitle, { color: textColor }]}>
              Pick a Color
            </Text>
            <TouchableOpacity onPress={onDismiss}>
              <Ionicons name="close" size={24} color={textMutedColor} />
            </TouchableOpacity>
          </View>

          {/* Preview swatch */}
          <View style={styles.previewRow}>
            <View style={[styles.previewSwatch, { backgroundColor: selectedColor }]} />
            <Text style={[styles.previewHex, { color: textMutedColor }]}>
              {selectedColor.toUpperCase()}
            </Text>
          </View>

          {/* Color picker */}
          <View style={styles.pickerSection}>
            <ColorPicker
              value={selectedColor}
              onComplete={onCompleteWorklet}
              style={styles.colorPicker}
            >
              <Panel1 style={styles.panel} />
              <HueSlider style={styles.hueSlider} />
            </ColorPicker>
          </View>

          {/* Preset swatches */}
          <View style={styles.presetsSection}>
            <Text style={[styles.presetsLabel, { color: textMutedColor }]}>
              Presets
            </Text>
            <PresetSwatchRow
              selectedColor={selectedColor}
              onSelect={onPresetSelect}
            />
          </View>

          {/* Done button */}
          <TouchableOpacity
            style={[styles.doneButton, { backgroundColor: accentColor }]}
            onPress={onDone}
            activeOpacity={0.8}
          >
            <Text style={styles.doneButtonText}>Done</Text>
          </TouchableOpacity>
        </Animated.View>
      </View>
    </Modal>
  );
}

type PresetSwatchRowProps = {
  selectedColor: string;
  onSelect: (hex: string) => void;
};

function PresetSwatchRow({ selectedColor, onSelect }: PresetSwatchRowProps) {
  return (
    <View style={styles.presetsRow}>
      {FAMILY_MEMBER_COLORS.map((color) => {
        const isSelected = color.hex.toLowerCase() === selectedColor.toLowerCase();
        return (
          <TouchableOpacity
            key={color.hex}
            onPress={() => onSelect(color.hex)}
            activeOpacity={0.7}
            style={[
              styles.presetSwatch,
              { backgroundColor: color.hex },
              isSelected && styles.presetSwatchSelected,
            ]}
          >
            {isSelected && (
              <Ionicons name="checkmark" size={16} color="#FFF" />
            )}
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  swatchButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
  },
  swatch: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 2,
    borderColor: 'rgba(0,0,0,0.1)',
  },
  swatchLabel: {
    fontSize: 14,
    fontWeight: '500',
  },
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  content: {
    borderTopLeftRadius: BorderRadius.xl,
    borderTopRightRadius: BorderRadius.xl,
    paddingBottom: Spacing.xxl,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: Spacing.xl,
    paddingVertical: Spacing.lg,
    borderBottomWidth: 1,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  previewRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.md,
    paddingHorizontal: Spacing.xl,
    paddingTop: Spacing.lg,
  },
  previewSwatch: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 2,
    borderColor: 'rgba(0,0,0,0.1)',
  },
  previewHex: {
    fontSize: 14,
    fontWeight: '600',
    letterSpacing: 0.5,
  },
  pickerSection: {
    paddingHorizontal: Spacing.xl,
    paddingTop: Spacing.lg,
  },
  colorPicker: {
    gap: Spacing.lg,
  },
  panel: {
    height: 180,
    borderRadius: BorderRadius.md,
  },
  hueSlider: {
    height: 32,
    borderRadius: BorderRadius.md,
  },
  presetsSection: {
    paddingHorizontal: Spacing.xl,
    paddingTop: Spacing.xl,
    gap: Spacing.sm,
  },
  presetsLabel: {
    fontSize: 13,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  presetsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.sm,
  },
  presetSwatch: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: 'rgba(0,0,0,0.1)',
  },
  presetSwatchSelected: {
    borderColor: 'rgba(0,0,0,0.4)',
  },
  doneButton: {
    marginHorizontal: Spacing.xl,
    marginTop: Spacing.xl,
    paddingVertical: Spacing.md,
    borderRadius: BorderRadius.md,
    alignItems: 'center',
  },
  doneButtonText: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: '600',
  },
});
